<template>
  <div class="financial-data">
    <h4 class="financial-data__title title">Dados financeiros</h4>
    <form class="financial-data__form grid form" @submit.prevent="handleSubmit">
      <label class="financial-data__protections-value form__label">
        <span class="form__label--only">
          Valor das suas proteções selecionado:
          <strong
            class="simulation-form__selected-protections-value form__label--emphasys"
          >{{ formattedProtectionsValue }}</strong>
        </span>
        <input
          :class="`form__input--range form__input--range-${setRangeBackgroundPercent}`"
          type="range"
          name="protectionsValue"
          v-model="financial.protectionsValue"
          :min="range.min"
          :max="range.max"
          :step="range.step"
          @change="handleRange"
          @input="handleStep"
        />
        <span class="form__error-message"></span>
      </label>
      <div
        :class="`financial-data__selected-insurer-shelf ${openChangeOptions && 'change-insurer-opened'}`"
      >
        <SimulationResultsShelf
          title="Seguradora selecionada:"
          showResults="Selected"
          v-on:changeOptionsInsurer="toggleChangeOptionsInsurer"
        />
      </div>
      <label class="financial-data__profession form__label form__group--column">
        <span class="form__label-text">Profissão</span>
        <input
          class="form__input"
          type="text"
          name="profession"
          v-model="financial.profession"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.profession === false,
              icon__check : validation.profession
            }"
          />
        </span>
      </label>
      <label class="financial-data__monthly-wins form__label form__group--column">
        <span class="form__label-text form__label-text--fullfiled">Renda mensal</span>
        <input
          class="form__input"
          type="text"
          name="monthlyWins"
          v-model="financial.monthlyWins"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.monthlyWins === false,
              icon__check : validation.monthlyWins
            }"
          />
        </span>
      </label>
      <label class="financial-data__politically-exposed form__label--checkbox-row">
        <input
          class="form__input--checkbox"
          type="checkbox"
          name="politicallyExposed"
          v-model="financial.politicallyExposed"
          @focus="handleFocus"
          @input="handleInput"
          @change="handleUserState"
        />
        <i class="form__label--checkbox-row-icon"></i>
        <span class="form__label--checkbox-row-text">
          Você é politicamente
          <br />
          {{ this.gender === "M" ? "exposto" : "exposta" }}?
        </span>
        <button
          type="button"
          class="form__help"
          title="A participação refere-se ao percentual destinado aos beneficiários em caso de falecimento do titular"
          aria-label="Botão de ajuda para saber mais detalhes relacionado ao campo a ser preenchido"
          @click="setLightbox(
            { 
              show: true,
              title: 'O que é uma Pessoa Politicamente Exposta?',
              content: `
                <p>É considerada um Pessoa Politicamente Exposta (PPE) ou Pessoa Exposta Politicamente (PEP), aquela que desempenha ou desempenhou nos últimos cinco anos anteriores, no Brasil ou em países, territórios e dependências estrangeiros, cargos, empregos ou funções públicas relevantes.</p>
                <p>O cargo, emprego ou função pública relevante podem ser exercidos por chefes de estado e de governo, políticos de alto nível, altos servidores dos poderes públicos, magistrados ou militares de alto nível, dirigentes de empresas públicas ou dirigentes de partidos políticos.</p>
                <p>A denominação se estende aos representantes, familiares e outras pessoas de seu relacionamento próximo. Ou seja, esses indivíduos também são considerados PPEs.</p>
              `
            }
          )"
        >?</button>
      </label>
      <label class="financial-data__retired form__label--checkbox-row">
        <input
          class="form__input--checkbox"
          type="checkbox"
          name="retired"
          v-model="financial.retired"
          @focus="handleFocus"
          @input="handleInput"
          @change="handleUserState"
        />
        <i class="form__label--checkbox-row-icon"></i>
        <span
          class="form__label--checkbox-row-text"
        >{{ this.gender === "M" ? "Aposentado" : "Aposentada" }}?</span>
      </label>
      <h5
        class="financial-data__subtitle subtitle financial-data__subtitle--beneficiares"
      >Beneficiários</h5>
      <div class="financial-data__observation">
        <h6 class="financial-data__observation-title">Observações:</h6>
        <ul class="financial-data__observation-list">
          <li
            class="financial-data__observation-list-item"
          >A participação refere-se ao percentual de indenização destinado aos beneficiários em caso de falecimento do titular.</li>
          <li
            class="financial-data__observation-list-item"
          >Você pode designar até 4 beneficiários e os percentuais de participação do benefício (indenização) de cada um.</li>
          <li
            class="financial-data__observation-list-item"
          >Na ausência de designação destes, a indenização será paga de acordo com a legislação aplicável.</li>
        </ul>
      </div>
      <div class="financial-data__beneficiares-main-wrapper">
        <div
          v-for="(beneficiary, index) in financial.beneficiares"
          :key="`beneficiares_${index}`"
          class="financial-data__beneficiares-wrapper"
        >
          <button
            type="button"
            class="financial-data__remove-beneficiary"
            @click="removeBeneficiary(index)"
            aria-label="Botão para remover beneficiário da contratação"
          >
            <i class="icon__plus" />
          </button>
          <label class="financial-data__beneficiares-name form__label form__group--column">
            <span class="form__label-text">Nome</span>
            <input
              class="form__input"
              type="text"
              :name="`beneficiaryName_${index}`"
              v-model="financial.beneficiares[index].name"
              @focus="handleFocus"
              @input="handleInput"
              @blur="handleBlur($event, index)"
            />
            <span class="form__error-message"></span>
            <span class="form__validation">
              <i
                class="icon"
                :class="{
                  icon__times : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryName === false,
                  icon__check : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryName
                }"
              />
            </span>
          </label>
          <label class="financial-data__beneficiares-birthDate form__label form__group--column">
            <span class="form__label-text form__label-text--fullfiled">Nascido em</span>
            <input
              class="form__input"
              type="tel"
              placeholder="dd/mm/aaaa"
              :name="`beneficiaryBirthDate_${index}`"
              v-model="financial.beneficiares[index].birthDate"
              @focus="handleFocus"
              @input="handleInput"
              @blur="handleBlur($event, index)"
            />
            <span class="form__error-message"></span>
            <span class="form__validation">
              <i
                class="icon"
                :class="{
                  icon__times : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryBirthDate === false,
                  icon__check : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryBirthDate
                }"
              />
            </span>
          </label>
          <label
            class="financial-data__beneficiares-kinship form__label form__label--select form__group--column"
          >
            <span class="form__label-text">Parentesco</span>
            <select
              class="form__input"
              :name="`beneficiaryKinship_${index}`"
              v-model="financial.beneficiares[index].kinship"
              @focus="handleFocus"
              @input="handleInput"
              @blur="handleBlur($event, index)"
            >
              <option
                v-for="(kinship, index) in kinshipList"
                :key="`parentesco_${index}`"
                :value="kinship"
              >{{ kinship }}</option>
            </select>
            <span class="form__error-message"></span>
          </label>
          <div class="financial-data__beneficiares-participation form__label">
            <label class="form__label form__group--column">
              <span class="form__label-text">Participação (%)</span>
              <input
                class="form__input"
                type="number"
                min="0"
                max="100"
                step="10"
                :name="`beneficiaryParticipation_${index}`"
                v-model="financial.beneficiares[index].participation"
                @focus="handleFocus"
                @input="handleInput"
                @blur="handleBlur($event, index)"
                :data-hashelper="true"
              />
              <span class="form__error-message"></span>
              <span class="form__validation">
                <i
                  class="icon"
                  :class="{
                    icon__times : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryParticipation === false,
                    icon__check : validation.beneficiares[index] && validation.beneficiares[index].beneficiaryParticipation
                  }"
                />
              </span>
            </label>
          </div>
        </div>
      </div>
      <button
        v-if="financial.beneficiares.length < 4"
        type="button"
        class="financial-data__add-beneficiary"
        title="Adicionar novo beneficiário"
        @click="addNewBeneficiary"
        aria-label="Botão para adicionar novo beneficiário na contratação"
      >
        <i class="icon__plus" />
      </button>
      <label
        class="financial-data__payment-option form__label form__label--select form__group--column"
      >
        <span class="form__label-text">Selecione a forma de pagamento</span>
        <select
          class="form__input"
          name="bank"
          v-model="financial.paymentOption"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        >
          <option value="Boleto Bancario">Boleto Bancário</option>
          <option value="Debito em conta">Débito em conta</option>
        </select>
        <span class="form__error-message"></span>
      </label>
      <h5
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__subtitle subtitle financial-data__subtitle--bank-infos"
      >
        Dados bancários
        <!-- TODO: Refatorar o css desse botão, quando passar a lógica para o basic input -->
        <button
          type="button"
          class="form__help"
          title="A participação refere-se ao percentual destinado aos beneficiários em caso de falecimento do titular"
          aria-label="Botão de ajuda para saber mais detalhes relacionado ao campo a ser preenchido"
          @click="setLightbox(
            { 
              show: true,
              title: 'Não encontrou seu banco?',
              content: '<p>Se você não encontrou o seu banco no campo <strong>Selecione o seu banco</strong> abaixo, por favor selecione a opção para pagamento via boleto bancário.</p>'
            }
          )"
        >?</button>
      </h5>
      <label
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__bank form__label form__label--select form__group--column"
      >
        <span class="form__label-text">Selecione seu banco</span>
        <select
          class="form__input"
          name="bank"
          v-model="financial.bankInfos.bank"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        >
          <option
            v-for="(bank, index) in bankList.list"
            :key="`${bank}_${index}`"
            :value="bank"
          >{{ bank }}</option>
        </select>
        <span class="form__error-message"></span>
      </label>
      <label
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__agency form__label form__group--column"
      >
        <span class="form__label-text">Agência</span>
        <input
          class="form__input"
          type="number"
          name="agency"
          v-model="financial.bankInfos.agency"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.agency === false,
              icon__check : validation.agency
            }"
          />
        </span>
      </label>
      <label
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__checking-account form__label form__group--column"
      >
        <span class="form__label-text">Conta Corrente (com dígito)</span>
        <input
          class="form__input"
          type="tel"
          name="checkingAccount"
          v-model="financial.bankInfos.checkingAccount"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.checkingAccount === false,
              icon__check : validation.checkingAccount
            }"
          />
        </span>
      </label>
      <label
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__cpf-checking-accounter form__label form__group--column"
      >
        <span class="form__label-text">CPF</span>
        <input
          class="form__input"
          type="tel"
          name="cpfCheckingAccounter"
          v-model="financial.bankInfos.cpfCheckingAccounter"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          maxlength="14"
          required="required"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.cpfCheckingAccounter === false,
              icon__check : validation.cpfCheckingAccounter
            }"
          />
        </span>
        <span class="form__tip">*Somente números</span>
      </label>
      <label
        v-if="financial.paymentOption === 'Debito em conta'"
        class="financial-data__pay-day form__label form__group--column"
      >
        <span class="form__label-text">Melhor data para pagamento</span>
        <input
          class="form__input"
          type="number"
          min="1"
          max="31"
          name="payDay"
          v-model="financial.bankInfos.payDay"
          @focus="handleFocus"
          @input="handleInput"
          @blur="handleBlur"
          required="required"
        />
        <span class="form__error-message"></span>
        <span class="form__validation">
          <i
            class="icon"
            :class="{
              icon__times: validation.payDay === false,
              icon__check : validation.payDAy
            }"
          />
        </span>
      </label>
      <button
        type="submit"
        class="financial-data__submit form__submit"
        aria-label="Botão para finalizar a contratação do seguro"
        :class="{'loading-default': this.submitText === 'finalizando...'}"
      >{{ submitText }}</button>
    </form>
  </div>
</template>

<script>
import SimulationResultsShelf from "../shelves/SimulationResultsShelf";
import { mapState, mapGetters, mapActions } from "vuex";
import bankList from "../../assets/helpers/bankList";
import insurersCalculations from "../../mixins/insurersCalculations";
import cpfValidation from "../../mixins/cpfValidation";

export default {
  name: "FinancialDataForm",
  components: {
    SimulationResultsShelf
  },
  data() {
    return {
      financial: {
        protectionsValue: null,
        selectedInsurer: {
          id: null,
          logo: "",
          value: null
        },
        profession: "",
        monthlyWins: "",
        politicallyExposed: false,
        retired: false,
        beneficiares: [
          {
            name: "",
            birthDate: null,
            kinship: "",
            participation: null
          }
        ],
        paymentOption: "",
        bankInfos: {
          bank: "",
          agency: null,
          checkingAccount: null,
          cpfCheckingAccounter: "",
          payDay: null
        }
      },
      range: {
        min: 50000,
        max: 1000000,
        step: 50000
      },
      gender: "",
      age: "",
      kinshipList: [
        "cônjuge",
        "filha",
        "filho",
        "irmão",
        "irmã",
        "mãe",
        "pai",
        "outro"
      ],
      bankList,
      openChangeOptions: false,
      submitText: "finalizar contratação",
      validation: {
        profession: null,
        monthlyWins: null,
        politicallyExposed: false,
        retired: false,
        beneficiares: [
          {
            beneficiaryName: null,
            beneficiaryBirthDate: null,
            beneficiaryParticipation: null
          }
        ],
        paymentOption: null,
        bank: null,
        agency: null,
        checkingAccount: null,
        cpfCheckingAccounter: null,
        payDay: null
      }
    };
  },
  mixins: [insurersCalculations, cpfValidation],
  computed: {
    ...mapState(["quotation"]),
    ...mapGetters(["getUser", "getInsurer"]),
    setRangeBackgroundPercent: function() {
      return parseInt((this.financial.protectionsValue / this.range.max) * 100);
    },
    formattedProtectionsValue: function() {
      return (
        this.financial.protectionsValue &&
        `R$ ${this.financial.protectionsValue
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")},00`
      );
    }
  },
  methods: {
    ...mapActions([
      "setSimulationSetted",
      "setLocalSave",
      "setUserState",
      "setQuotationStepIncrement",
      "setSimulationState",
      "setQuotationState",
      "setQuotationFinished",
      "setSimulationValues",
      "setProtectionsValue",
      "setSelectedInsurer",
      "setNewBeneficiary",
      "setRemoveBeneficiary",
      "setQuotation",
      "setLightbox"
    ]),
    checkFieldAsTrue(event, index) {
      const {
        target: { name, value }
      } = event;

      this.handleUserState(event);
      this.validation[name] = true;
      const isValid = this.validation.beneficiares[index] 
                        && value !== "" 
                        && name.indexOf("beneficiary") >= 0;
      if (isValid) {
        const ref = name.split("_")[0];
        this.validation.beneficiares[index][ref] = true;
      }
    },
    setAge: function() {
      const {
        user: {
          personalData: { birthDate }
        }
      } = this.quotation;
      const birth = new Date(
        birthDate
          .split("/")
          .reverse()
          .join("/")
      );
      const today = new Date();
      this.age =
        today.getMonth() + 1 <= new Date(birth).getMonth() + 1
          ? today.getFullYear() - new Date(birth).getFullYear()
          : today.getFullYear() - new Date(birth).getFullYear();
    },
    financialLocalSave() {
      this.setLocalSave({
        storeItemName: "user",
        storeItemObject: this.quotation.user
      });
    },
    handleRange: function() {
      document.querySelector(".form__input--range").focus();
      const { selectedInsurer } = this.financial;

      const infos = {
        age: this.age,
        gender: this.gender,
        selectedValue: this.financial.protectionsValue
      };

      let payload = [
        {
          id: "bradescoSeguros",
          value: this.calculateBradescoSeguros(
            infos,
            this.getInsurer("bradescoSeguros").table
          )
        },
        {
          id: "icatuSeguros",
          value: this.calculateIcatuSeguros(
            infos,
            this.getInsurer("icatuSeguros").table
          )
        },
        {
          id: "mongeralAegon",
          value: this.calculateMongeralAegon(
            infos,
            this.getInsurer("mongeralAegon").table
          )
        },
        {
          id: "portoSeguro",
          value: this.calculatePortoSeguro(
            infos,
            this.getInsurer("portoSeguro").table
          )
        }
      ];
      this.setSimulationValues(payload);
      this.setProtectionsValue(this.financial.protectionsValue);
      const updateInsurer = payload.filter(
        insurer => insurer.id === selectedInsurer.id
      );
      this.setSelectedInsurer(updateInsurer[0]);
    },
    handleStep: function({ target }) {
      const { value } = target;
      if (value >= 400000) {
        this.range.min = this.range.step = 100000;
      } else {
        this.range.min = this.range.step = 50000;
      }
    },
    toggleChangeOptionsInsurer: function(open) {
      const header = document.querySelector(".header");
      const selectedInsurerWrapper = document.querySelector(
        ".financial-data__selected-insurer-shelf"
      );

      if (open) {
        scrollTo(
          0,
          selectedInsurerWrapper.offsetTop +
            selectedInsurerWrapper.offsetHeight -
            header.offsetHeight
        );
      } else {
        scrollTo(0, selectedInsurerWrapper.offsetTop - header.offsetHeight);
      }
      this.openChangeOptions = open;
    },
    handleUserState(event) {
      const payload = {
        event,
        group: "financialData"
      };
      this.setUserState(payload);
      this.financialLocalSave();
    },
    handleFocus: function({ target }) {
      const { dataset, previousElementSibling, type } = target;
      if (type !== "checkbox" && type !== "radio") {
        previousElementSibling.classList.add("form__label-text--focus");
      }
    },
    handleInput: function(event) {
      const {
        target: {
          parentElement,
          previousElementSibling,
          nextElementSibling,
          type,
          validity: { valid }
        }
      } = event;
      if (type !== "checkbox" && type !== "radio") {
        previousElementSibling.classList.add("form__label-text--fullfiled");
        if (valid) {
          parentElement.classList.remove("error");
          nextElementSibling.innerText = "";
        }
      }
    },
    handleBlur: function(event, index) {
      const {
        target: {
          dataset,
          name,
          parentElement,
          previousElementSibling,
          nextElementSibling,
          type,
          validity: { typeMismatch, valid, valueMissing },
          value
        }
      } = event;
      previousElementSibling.classList.remove("form__label-text--focus");

      if (!valid) {
        
        this.validation[name] = false;
        if (type === "email" && typeMismatch) {
          nextElementSibling.innerText = "Favor preencher com um e-mail válido";
        } else if (valueMissing) {
          nextElementSibling.innerText = "Campo obrigatório";
        } else if (
          name.indexOf("beneficiaryParticipation") >= 0 &&
          value > 100
        ) {
          nextElementSibling.innerText =
            "Favor informar um percentual entre 0 e 100";
        }
        parentElement.classList.add("error");
      } else {
        if (value === "" && name.indexOf("beneficiary") >= 0) {
          const ref = name.split("_")[0];
          if(this.validation.beneficiares[index]) {
              this.validation.beneficiares[index][ref] = false;
            }
        }
        if (valueMissing) {
          this.validation[name] = false;

          previousElementSibling.classList.remove(
            "form__label-text--fullfiled"
          );
        } else if (name.indexOf("beneficiaryBirthDate") >= 0) {
          const pattern = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
          const valueSplitted = value.split("").filter(char => char !== "/");
          const slashQty = value.split("").filter(char => char === "/").length;
          const index = name.split("_")[1];

          if (slashQty < 2) {
            valueSplitted.splice(2, 0, "/");
            valueSplitted.splice(5, 0, "/");
            event.target.value = this.financial.beneficiares[index].birthDate = valueSplitted.join("");
          }
          if (!pattern.test(event.target.value)) {
            parentElement.classList.add("error");
            nextElementSibling.innerText =
              'Favor preencher a data no padrão "dd/mm/aaaa"';
          } else {
            this.checkFieldAsTrue(event, index);
          }
        } else if (name === "cpfCheckingAccounter") {
          const cpfCheckingAccounter = this.financial.bankInfos
            .cpfCheckingAccounter;

          this.financial.bankInfos.cpfCheckingAccounter = this.cpfLeftZeros(
            value
          );
          this.financial.bankInfos.cpfCheckingAccounter = this.cpfMask(
            cpfCheckingAccounter
          );
          if (!this.validateCpf(cpfCheckingAccounter)) {
            parentElement.classList.add("error");
            nextElementSibling.innerText = "Por favor insira um CPF válido";
            document
              .querySelector(".financial-data__submit")
              .setAttribute("disabled", "disabled");
            this.validation[name] = false;
          } else {
            parentElement.classList.remove("error");
            nextElementSibling.innerText = "";
            document
              .querySelector(".financial-data__submit")
              .removeAttribute("disabled");
            
            this.checkFieldAsTrue(event, index);
          }
        } else {
          this.checkFieldAsTrue(event, index);
        }
      }
    },
    addNewBeneficiary: function() {
      this.setNewBeneficiary();
      this.financialLocalSave();
      this.validation.beneficiares.push({
        beneficiaryName: null,
        beneficiaryBirthDate: null,
        beneficiaryParticipation: null
      });
    },
    removeBeneficiary: function(index) {
      this.setRemoveBeneficiary(index);
      this.financialLocalSave();
    },
    handleSubmit: function() {
      document
        .querySelector(".form__submit")
        .setAttribute("disabled", "disabled");
      this.setQuotationFinished(true);
      const user = this.getUser;
      this.$router.push("/cotacao/enviada")
    }
  },
  mounted() {
    this.setSimulationSetted(true);
    if (localStorage.localSimulationState) {
      this.setSimulationState(localStorage.localSimulationState);
    }
    this.financial = this.quotation.user.financialData;
    this.gender = this.quotation.user.personalData.gender;
    setTimeout(() => {
      const formInputs = document.querySelectorAll("form input");
      const avoidTypes = ["checkbox", "radio", "range"];
      formInputs.forEach(input => {
        if (
          avoidTypes.indexOf(input.type) < 0 &&
          input.value !== "" &&
          input.value !== null
        ) {
          input.previousElementSibling.classList.add(
            "form__label-text--fullfiled"
          );
        }
      });
    }, 500);
    this.setAge();
  }
};
</script>

<style lang="scss">
.financial-data {
  @include fade-in();
  animation-delay: 0.2s;

  &__title {
    margin-bottom: $space__lg;
  }

  &__subtitle {
    grid-area: subtitle;
    margin-bottom: $space__st;
    font-size: $font-size__st;

    &--beneficiares {
      grid-area: subtitle-beneficiares;
    }

    &--bank-infos {
      position: relative;
      grid-area: subtitle-bank-infos;
    }
  }

  &__form {
    grid-row-gap: $space__sm;
    grid-template-areas:
      "protections-value protections-value protections-value"
      "selected-insurer-shelf selected-insurer-shelf selected-insurer-shelf"
      "change-insurer-opened change-insurer-opened change-insurer-opened"
      "profession profession profession"
      "monthly-wins monthly-wins monthly-wins"
      "politically-exposed politically-exposed politically-exposed"
      "retired retired retired"
      "subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares"
      "observation-beneficiaries observation-beneficiaries observation-beneficiaries"
      "beneficiares beneficiares beneficiares"
      "add-beneficiary add-beneficiary add-beneficiary"
      "payment payment payment"
      "subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos"
      "bank bank bank"
      "agency agency agency"
      "checking-account checking-account checking-account"
      "cpf-checking-accounter cpf-checking-accounter cpf-checking-accounter"
      "pay-day pay-day pay-day"
      "submit submit submit";

    @include breakpoint($tablet__min--sm) {
      grid-row-gap: $space__md;
      grid-template-areas:
        "protections-value protections-value selected-insurer-shelf"
        "change-insurer-opened change-insurer-opened change-insurer-opened"
        "profession profession monthly-wins"
        "politically-exposed politically-exposed retired"
        "subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares"
        "observation-beneficiaries observation-beneficiaries observation-beneficiaries"
        "beneficiares beneficiares beneficiares"
        "add-beneficiary add-beneficiary add-beneficiary"
        "payment payment payment"
        "subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos"
        "bank bank agency"
        "checking-account checking-account pay-day"
        "cpf-checking-accounter cpf-checking-accounter cpf-checking-accounter"
        ". submit .";
    }

    @include breakpoint($desk__min--sm) {
      grid-row-gap: $space__st;
      grid-template-areas:
        "protections-value . . selected-insurer-shelf selected-insurer-shelf"
        "change-insurer-opened change-insurer-opened change-insurer-opened change-insurer-opened change-insurer-opened"
        "profession monthly-wins . politically-exposed retired"
        "subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares"
        "observation-beneficiaries observation-beneficiaries observation-beneficiaries observation-beneficiaries observation-beneficiaries"
        "beneficiares beneficiares beneficiares beneficiares beneficiares"
        "add-beneficiary . . . ."
        "payment . . . ."
        "subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos"
        "bank agency checking-account pay-day cpf-checking-accounter"
        ". . submit . .";
    }

    @include breakpoint($desk__min--lg) {
      grid-row-gap: $space__st;
      grid-template-areas:
        "protections-value . . selected-insurer-shelf selected-insurer-shelf"
        "change-insurer-opened change-insurer-opened change-insurer-opened change-insurer-opened change-insurer-opened"
        "profession monthly-wins . politically-exposed retired"
        "subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares subtitle-beneficiares"
        "observation-beneficiaries observation-beneficiaries observation-beneficiaries observation-beneficiaries observation-beneficiaries"
        "beneficiares beneficiares beneficiares beneficiares add-beneficiary"
        "payment . . . ."
        "subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos subtitle-bank-infos"
        "bank agency checking-account pay-day cpf-checking-accounter"
        ". . submit . .";
    }
  }
  &__protections-value {
    grid-area: protections-value;

    @include breakpoint($tablet__min--sm) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }
  &__selected-insurer-shelf {
    grid-area: selected-insurer-shelf;
    margin-bottom: $space__lg;
    &.change-insurer-opened {
      grid-area: change-insurer-opened;
    }
  }
  &__profession {
    grid-area: profession;
  }
  &__monthly-wins {
    grid-area: monthly-wins;
  }
  &__politically-exposed {
    grid-area: politically-exposed;
  }
  &__retired {
    grid-area: retired;
  }
  &__observation {
    padding: rem(10);
    grid-area: observation-beneficiaries;
    border: 2px solid $color__primary;

    &-title {
      margin-bottom: rem(10);
      font-size: $font-size__lg;
    }
    &-list {
      padding-left: rem(20);
      list-style: disc;

      &-item {
        margin-bottom: rem(10);
      }
    }
  }
  &__beneficiares {
    &-main-wrapper {
      grid-area: beneficiares;
    }
    &-wrapper {
      @extend %card;
      position: relative;
      display: flex;
      flex-wrap: wrap;
      padding: $space__st;

      &:first-of-type {
        .financial-data__remove-beneficiary {
          display: none;
        }
      }
    }
    &-name {
      flex: 0 0 90%;
    }
    &-birthDate {
      flex: 0 0 90%;
    }
    &-kinship {
      flex: 1 1 40%;
    }
    &-participation {
      position: relative;
      margin-left: $space__xs;
      flex: 1 1 40%;
    }
  }
  &__remove-beneficiary {
    @extend %button__terciary--round-small;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(50%, -50%);

    .icon__plus {
      &:before {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
      }
    }
  }
  &__add-beneficiary {
    @extend %button__primary--round;
    position: relative;
    grid-area: add-beneficiary;
    margin-bottom: $space__lg;
    justify-self: flex-start;
    align-self: center;
    transition: all 0.2s ease-in;

    &:focus,
    &:hover {
      box-shadow: 5px 5px 7px rgba($color__black, 0.5);
      transform: translate(-$space__xs, -$space__xs);
    }

    &:after {
      content: "adicionar novo beneficiário";
      display: block;
      position: absolute;
      top: 50%;
      left: 110%;
      width: 27ch;
      color: $color__black;
      text-transform: lowercase;
      transform: translateY(-50%);
    }

    @include breakpoint-range($tablet__min--sm, $tablet__max--lg) {
      margin-bottom: 0;
      justify-self: center;
      align-self: center;

      &:after {
        top: 0;
        left: 50%;
        transform: translate(-50%, -150%);
      }
    }
    @include breakpoint($desk__min--lg) {
      justify-self: center;
    }
    @include breakpoint($desk__min--lg) {
      justify-self: flex-start;

      &:after {
        top: 50%;
        left: 130%;
        transform: translateY(-50%);
      }
    }
  }
  &__payment-option {
    grid-area: payment;
  }
  &__bank {
    grid-area: bank;
    @include fade-in;
  }
  &__agency {
    grid-area: agency;
    @include fade-in;
  }
  &__checking-account {
    grid-area: checking-account;
    @include fade-in;
  }
  &__cpf-checking-accounter {
    grid-area: cpf-checking-accounter;
    @include fade-in;
  }
  &__pay-day {
    grid-area: pay-day;
    @include fade-in;
  }
  &__submit {
    grid-area: submit;
  }
}
</style>
